import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError } from '../lib/api';
import {
  type ConnectionState,
  type EfacturaFilters,
  type EfacturaKind,
  type EfacturaMessageRow,
  type EfacturaTotal,
} from '../lib/efacturaApi';
import {
  DetailFrame,
  EfacturaFileButtons,
  FIELD_CLASS,
  InvoiceDetails,
  KIND_LABELS,
  KindBadge,
  SettledSwitch,
  date,
  dateTime,
  money,
  useEfacturaFiles,
} from '../components/domain/efactura/EfacturaShared';
import {
  useEfacturaCompanies,
  useEfacturaMessage,
  useEfacturaMessages,
  useEfacturaStatus,
  useSetEfacturaSettled,
  useSyncEfactura,
} from '../hooks/useEfactura';
import { useInvoiceSyncWatcher } from '../hooks/useInvoices';
import { useActiveCompanyId } from '../store/useCompanyStore';
import { usePermissions } from '../hooks/usePermissions';
import { Select } from '../components/ui/Select';
import { DateField } from '../components/ui/DateField';
import { FadeIn, SkeletonTable } from '../components/ui/Skeleton';

// FINANCIAR › SPV e-Factura. Lists the messages mirrored from ANAF SPV for the
// selected firm (or every firm), with the parsed invoice on demand and the
// PDF / XML / signed archive as downloads. The mirror is read-only: nothing on
// this page writes to ANAF.

const DEFAULT_FILTERS: EfacturaFilters = { page: 1, limit: 50 };

const TABS: Array<{ key: string; label: string; kind: EfacturaKind | undefined }> = [
  { key: 'ALL', label: 'Toate', kind: undefined },
  { key: 'PRIMITA', label: 'Primite', kind: 'PRIMITA' },
  { key: 'TRIMISA', label: 'Trimise', kind: 'TRIMISA' },
  { key: 'ERORI', label: 'Respinse', kind: 'ERORI' },
  { key: 'MESAJ_CUMPARATOR', label: 'Mesaje', kind: 'MESAJ_CUMPARATOR' },
];

function StatusBanner({
  state,
  message,
  appConfigured,
  companyId,
}: {
  state: ConnectionState;
  message: string;
  appConfigured: boolean;
  companyId: string;
}) {
  const style =
    state === 'connected'
      ? 'border-green-200 bg-green-50 text-green-800'
      : state === 'pending' || state === 'not_configured'
        ? 'border-amber-200 bg-amber-50 text-amber-800'
        : 'border-red-200 bg-red-50 text-red-800';
  const title =
    state === 'connected'
      ? 'SPV este conectat.'
      : state === 'pending'
        ? 'Firma nu este încă autorizată la ANAF.'
        : state === 'not_configured'
          ? 'SPV nu este configurat pentru această firmă.'
          : state === 'needs_reauth'
            ? 'Autorizarea la ANAF a expirat.'
            : state === 'no_spv_rights'
              ? 'Persoana autorizată nu are drepturi în SPV pe acest CIF.'
              : 'Conexiunea SPV are o problemă.';
  return (
    <div className={`rounded-md border px-3 py-2 text-sm ${style}`}>
      <strong>{title}</strong> {message}{' '}
      {!appConfigured && (
        <span>
          Aplicația OAuth ANAF lipsește din{' '}
          <Link to="/settings" className="underline">
            Setări → Integrări
          </Link>
          .
        </span>
      )}
      {state !== 'connected' && (
        <span>
          {' '}
          Configurezi și autorizezi din{' '}
          <Link to={`/settings/companies/${companyId}`} className="underline">
            pagina firmei
          </Link>
          .
        </span>
      )}{' '}
      Facturile de mai jos sunt cele deja aduse din SPV; ANAF le păstrează doar 60 de zile, aici
      rămân.
    </div>
  );
}

function TotalsRow({ totals }: { totals: EfacturaTotal[] }) {
  const cells = totals
    .filter((t) => t.kind === 'PRIMITA' || t.kind === 'TRIMISA')
    .sort(
      (a, b) => a.kind.localeCompare(b.kind) || (a.currency ?? '').localeCompare(b.currency ?? ''),
    );
  if (cells.length === 0) return null;
  return (
    <div className="grid grid-cols-2 gap-3 text-sm sm:flex sm:flex-wrap">
      {cells.map((t) => (
        <div
          key={`${t.kind}-${t.currency}`}
          className="rounded-md border border-gray-200 bg-white px-3 py-2"
        >
          <div className="text-xs uppercase tracking-wide text-gray-500">
            {KIND_LABELS[t.kind]} · {t.currency ?? '?'}
          </div>
          <div className="font-semibold text-gray-900">{money(t.totalAmount, t.currency)}</div>
          <div className="text-xs text-gray-500">{t.count} documente</div>
        </div>
      ))}
    </div>
  );
}

function MessageDetail({ row, onClose }: { row: EfacturaMessageRow; onClose: () => void }) {
  const detail = useEfacturaMessage(row.id);
  const files = useEfacturaFiles(row.id, row.anafId);
  const isInvoice = row.docType === 'factura' || row.docType === 'storno';

  return (
    <DetailFrame
      colSpan={9}
      title={
        <>
          Mesaj SPV <span className="font-mono">{row.anafId}</span>
          {row.indexIncarcare && (
            <>
              {' '}
              · index încărcare <span className="font-mono">{row.indexIncarcare}</span>
            </>
          )}{' '}
          · {dateTime(row.dataCreare)}
        </>
      }
      actions={
        <EfacturaFileButtons
          hasInvoice={isInvoice}
          busy={files.busy}
          onOpen={(kind) => void files.open(kind)}
        />
      }
      error={files.fileError}
      notice={row.syncError}
      onClose={onClose}
    >
      {detail.isLoading && <p className="text-sm text-gray-500">Se încarcă…</p>}
      {detail.data?.details ? (
        <InvoiceDetails doc={detail.data.details} />
      ) : (
        !detail.isLoading && (
          <p className="text-sm text-gray-500">
            Documentul nu a putut fi interpretat. Descarcă XML-ul sau arhiva.
          </p>
        )
      )}
    </DetailFrame>
  );
}

export function SpvPage() {
  const activeCompanyId = useActiveCompanyId() ?? undefined;
  const { can } = usePermissions();
  const canSync = can('financials', 'canSyncSpv');
  const canSettle = can('financials', 'canEditInvoices');
  const setSettled = useSetEfacturaSettled();
  const [filters, setFilters] = useState<EfacturaFilters>(DEFAULT_FILTERS);
  const [openId, setOpenId] = useState<string | null>(null);
  const [syncMessage, setSyncMessage] = useState<string | null>(null);

  const companies = useEfacturaCompanies();
  // "All firms": show the state of the first configured firm; the list below
  // still spans every firm.
  const statusCompanyId = activeCompanyId ?? companies.data?.[0]?.companyId ?? undefined;
  const status = useEfacturaStatus(
    statusCompanyId,
    Boolean(statusCompanyId) || activeCompanyId === undefined,
  );
  const messages = useEfacturaMessages(filters);
  const sync = useSyncEfactura();
  // Background scheduler runs refetch this list too (same watcher as Facturi).
  useInvoiceSyncWatcher();

  const rows = messages.data?.data ?? [];
  const meta = messages.data?.meta;
  const totals = messages.data?.totals ?? [];
  const companyNames = useMemo(
    () => new Map((companies.data ?? []).map((c) => [c.companyId, c.name])),
    [companies.data],
  );

  function patch(p: Partial<EfacturaFilters>) {
    setFilters((prev) => ({ ...prev, ...p, page: 1 }));
    setOpenId(null);
  }

  function handleSync() {
    setSyncMessage(null);
    sync.mutate(
      { companyId: activeCompanyId },
      {
        onSuccess: (result) => {
          const parts = result.companies.map(
            (c) =>
              `${companyNames.get(c.companyId) ?? c.cif}: ${c.listed} în SPV, ${c.created} noi, ${c.updated} actualizate` +
              (c.failed ? `, ${c.failed} cu erori` : '') +
              (c.partial ? ` — oprit: ${c.stopReason ?? ''}` : ''),
          );
          const skipped = result.skipped.map(
            (s) =>
              `${companyNames.get(s.companyId) ?? s.companyId}: ${s.reason === 'pending' ? 'neautorizată' : s.reason}`,
          );
          setSyncMessage(
            [
              parts.length
                ? `Sincronizat. ${parts.join(' · ')}`
                : 'Nicio firmă autorizată de sincronizat.',
              ...skipped,
            ].join(' · '),
          );
        },
        onError: (err) =>
          setSyncMessage(err instanceof ApiError ? err.message : 'Sincronizarea a eșuat.'),
      },
    );
  }

  const st = status.data;

  return (
    <div className="w-full p-4 sm:p-6 lg:p-8">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">SPV e-Factura</h1>
          <p className="text-sm text-gray-500">
            Facturile primite și trimise prin ANAF SPV, aduse automat în Financiar. Doar citire.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {canSync && (
            <button
              type="button"
              onClick={handleSync}
              disabled={sync.isPending}
              className="rounded bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {sync.isPending ? 'Se sincronizează…' : 'Sincronizează din SPV'}
            </button>
          )}
        </div>
      </div>

      <div className="mt-4 space-y-2">
        {st && (
          <StatusBanner
            state={st.connection.state}
            message={st.connection.message}
            appConfigured={st.appConfigured}
            companyId={st.companyId}
          />
        )}
        {status.isError && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
            {status.error instanceof ApiError
              ? status.error.message
              : 'Starea conexiunii nu a putut fi citită.'}
          </div>
        )}
        {syncMessage && (
          <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
            {syncMessage}
          </div>
        )}
        {st?.lastSyncAt && (
          <p className="text-xs text-gray-500">
            Ultima sincronizare: {dateTime(st.lastSyncAt)} · {st.cachedMessages} mesaje stocate
          </p>
        )}
      </div>

      <div className="mt-4">
        <TotalsRow totals={totals} />
      </div>

      {/* One scrolling row on phones: wrapped, "Mesaje" sat alone on a
          second line under the others. */}
      <div className="mt-6 flex gap-1 overflow-x-auto border-b border-gray-200">
        {TABS.map((tab) => {
          const active = (filters.kind ?? undefined) === tab.kind;
          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => patch({ kind: tab.kind })}
              className={`-mb-px shrink-0 whitespace-nowrap border-b-2 px-3 py-2 text-sm ${
                active
                  ? 'border-blue-600 font-medium text-blue-700'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Phones: two equal columns (search and the reset across the row)
          instead of fields of five different widths. */}
      <div className="mt-4 grid grid-cols-2 items-end gap-3 sm:flex sm:flex-wrap">
        <div className="col-span-2 sm:w-56">
          <label className="ui-field-label">Caută</label>
          <input
            type="search"
            value={filters.search ?? ''}
            onChange={(e) => patch({ search: e.target.value })}
            placeholder="Partener, CIF, număr…"
            className={`${FIELD_CLASS} w-full`}
          />
        </div>
        <label className="flex flex-col gap-1 text-xs font-medium text-gray-600">
          De la
          <DateField
            value={filters.from ?? ''}
            onChange={(v) => patch({ from: v || undefined })}
            className="w-full sm:w-36"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs font-medium text-gray-600">
          Până la
          <DateField
            value={filters.to ?? ''}
            onChange={(v) => patch({ to: v || undefined })}
            className="w-full sm:w-36"
          />
        </label>
        <div className="sm:w-40">
          <Select
            label="Status plată"
            size="sm"
            value={filters.settled ?? ''}
            onChange={(v) => patch({ settled: v === 'true' || v === 'false' ? v : undefined })}
            options={[
              { value: '', label: 'Toate' },
              { value: 'false', label: 'Deschise' },
              { value: 'true', label: 'Achitate / încasate' },
            ]}
            className="w-full"
          />
        </div>
        <div className="sm:w-32">
          <Select
            label="Pe pagină"
            size="sm"
            value={String(filters.limit ?? 50)}
            onChange={(v) => patch({ limit: Number(v) || 50 })}
            options={[
              { value: '25', label: '25' },
              { value: '50', label: '50' },
              { value: '100', label: '100' },
            ]}
            className="w-full"
          />
        </div>
        <button
          type="button"
          onClick={() => {
            setFilters(DEFAULT_FILTERS);
            setOpenId(null);
          }}
          className="col-span-2 rounded border border-gray-300 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50 sm:col-span-1"
        >
          Șterge
        </button>
      </div>

      <div className="mt-6">
        {messages.isLoading && <SkeletonTable rows={8} columns={9} />}
        {messages.isError && (
          <p className="text-red-600">
            Nu s-au putut încărca mesajele
            {messages.error instanceof Error ? `: ${messages.error.message}` : ''}.
          </p>
        )}
        {!messages.isLoading && !messages.isError && (
          <FadeIn>
            {rows.length === 0 ? (
              <p className="py-8 text-center text-gray-500">
                Niciun mesaj SPV stocat pentru filtrele alese.
                {canSync && ' Apasă „Sincronizează din SPV” după ce firma este autorizată.'}
              </p>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-gray-200">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">Tip</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">Număr</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">
                        Data facturii
                      </th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">Partener</th>
                      <th className="px-3 py-2 text-right font-medium text-gray-600">Fără TVA</th>
                      <th className="px-3 py-2 text-right font-medium text-gray-600">TVA</th>
                      <th className="px-3 py-2 text-right font-medium text-gray-600">Total</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">În SPV din</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">
                        {filters.kind === 'TRIMISA'
                          ? 'Încasată'
                          : filters.kind === 'PRIMITA'
                            ? 'Achitată'
                            : 'Plată'}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {rows.map((row) => {
                      const isOpen = openId === row.id;
                      return (
                        <FragmentRow
                          key={row.id}
                          row={row}
                          isOpen={isOpen}
                          companyLabel={
                            !activeCompanyId ? companyNames.get(row.companyId) : undefined
                          }
                          onToggle={() => setOpenId(isOpen ? null : row.id)}
                          canSettle={canSettle}
                          settling={setSettled.isPending && setSettled.variables?.id === row.id}
                          onSettle={(settled) => setSettled.mutate({ id: row.id, settled })}
                        />
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {meta && meta.totalPages > 1 && (
              <div className="mt-4 flex items-center justify-between text-sm text-gray-600">
                <span>
                  Pagina {meta.page} din {meta.totalPages} ({meta.total} în total)
                </span>
                <div className="flex gap-2">
                  <button
                    type="button"
                    disabled={meta.page <= 1}
                    onClick={() => setFilters((p) => ({ ...p, page: (p.page ?? 1) - 1 }))}
                    className="rounded border border-gray-300 px-3 py-1 disabled:opacity-50"
                  >
                    Anterior
                  </button>
                  <button
                    type="button"
                    disabled={meta.page >= meta.totalPages}
                    onClick={() => setFilters((p) => ({ ...p, page: (p.page ?? 1) + 1 }))}
                    className="rounded border border-gray-300 px-3 py-1 disabled:opacity-50"
                  >
                    Următorul
                  </button>
                </div>
              </div>
            )}
          </FadeIn>
        )}
      </div>
    </div>
  );
}

function FragmentRow({
  row,
  isOpen,
  companyLabel,
  onToggle,
  canSettle,
  settling,
  onSettle,
}: {
  row: EfacturaMessageRow;
  isOpen: boolean;
  companyLabel?: string;
  onToggle: () => void;
  canSettle: boolean;
  settling: boolean;
  onSettle: (settled: boolean) => void;
}) {
  // Hover and the open row use the theme-mapped ramps (gray → neutral, brand →
  // primary). `blue-*` is a static Tailwind palette, so the previous
  // `hover:bg-blue-50` painted a near-white row under the dark theme and the
  // light text on top of it disappeared.
  const rowClass = [
    'cursor-pointer hover:bg-gray-200',
    isOpen ? 'bg-brand-100' : '',
    row.syncError ? 'text-amber-800' : '',
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <>
      <tr onClick={onToggle} className={rowClass}>
        <td className="px-3 py-2">
          <KindBadge kind={row.kind} storno={row.docType === 'storno'} />
        </td>
        <td className="px-3 py-2 font-medium text-gray-900">
          {row.invoiceNumber ?? (
            <span className="font-mono text-xs text-gray-500">{row.anafId}</span>
          )}
          {companyLabel && (
            <span className="block text-xs font-normal text-gray-500">{companyLabel}</span>
          )}
        </td>
        <td className="px-3 py-2 text-gray-700">{date(row.issueDate)}</td>
        <td className="px-3 py-2 text-gray-700">
          {row.partnerName ?? '—'}
          {row.partnerCif && (
            <span className="block text-xs text-gray-500">CIF {row.partnerCif}</span>
          )}
        </td>
        <td className="px-3 py-2 text-right text-gray-700">{money(row.netAmount, row.currency)}</td>
        <td className="px-3 py-2 text-right text-gray-700">{money(row.vatAmount, row.currency)}</td>
        <td className="px-3 py-2 text-right font-medium text-gray-900">
          {money(row.totalAmount, row.currency)}
        </td>
        <td className="px-3 py-2 text-gray-500">{dateTime(row.dataCreare)}</td>
        <td className="px-3 py-2">
          <SettledSwitch
            label={row.kind === 'TRIMISA' ? 'Încasată' : row.kind === 'PRIMITA' ? 'Achitată' : null}
            settledAt={row.settledAt}
            canSettle={canSettle}
            settling={settling}
            onSettle={onSettle}
          />
        </td>
      </tr>
      {isOpen && <MessageDetail row={row} onClose={onToggle} />}
    </>
  );
}
